import MarkdownIt from 'markdown-it';
import type Token from 'markdown-it/lib/token.mjs';
import markdownItDeflist from 'markdown-it-deflist';
import markdownItFootnote from 'markdown-it-footnote';
import markdownItTexmath from 'markdown-it-texmath';
import katex from 'katex';

export type EditorMark = { type: string; attrs?: Record<string, unknown> };
export type EditorNode = { type: string; attrs?: Record<string, unknown>; content?: EditorNode[]; text?: string; marks?: EditorMark[] };

const markdown = new MarkdownIt({ html: true, linkify: true, typographer: false })
  .use(markdownItFootnote)
  .use(markdownItDeflist)
  .use(markdownItTexmath, { engine: katex, delimiters: 'dollars' });

function textNode(text: string, marks: EditorMark[] = []): EditorNode[] {
  if (!text) return [];
  const nodes: EditorNode[] = [];
  const special = /\[@([A-Za-z0-9_.:/-]+)(?:,\s*([^\]]+))?\]|\+\+([^+\n]+)\+\+|==([^=\n]+)==/g;
  let cursor = 0;
  for (let match = special.exec(text); match; match = special.exec(text)) {
    if (match.index > cursor) nodes.push({ type: 'text', text: text.slice(cursor, match.index), marks: marks.length ? [...marks] : undefined });
    if (match[1]) nodes.push({ type: 'citation', attrs: { citationKey: match[1], suffix: match[2] || null } });
    else nodes.push({ type: 'text', text: match[3] || match[4], marks: [...marks, match[3] ? { type: 'underline' } : { type: 'highlight', attrs: { color: null } }] });
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) nodes.push({ type: 'text', text: text.slice(cursor), marks: marks.length ? [...marks] : undefined });
  return nodes;
}

function inlineNodes(token: Token): EditorNode[] {
  const result: EditorNode[] = [];
  const marks: EditorMark[] = [];
  const children = token.children || [];
  for (const child of children) {
    if (child.type === 'text') result.push(...textNode(child.content, marks));
    else if (child.type === 'softbreak') result.push(...textNode(' ', marks));
    else if (child.type === 'hardbreak') result.push({ type: 'hardBreak' });
    else if (child.type === 'code_inline') result.push({ type: 'text', text: child.content, marks: [...marks, { type: 'code' }] });
    else if (child.type === 'html_inline') result.push({ type: 'text', text: child.content, marks: [...marks, { type: 'code' }] });
    else if (child.type === 'math_inline') result.push({ type: 'inlineMath', attrs: { source: child.content } });
    else if (child.type === 'footnote_ref') result.push({ type: 'footnoteReference', attrs: { label: String(child.meta?.label || Number(child.meta?.id || 0) + 1) } });
    else if (child.type === 'image') result.push({
      type: 'image',
      attrs: { src: child.attrGet('src'), alt: child.content || child.attrGet('alt') || null, title: child.attrGet('title') || null },
    });
    else if (child.type.endsWith('_open')) {
      if (child.type === 'strong_open') marks.push({ type: 'bold' });
      else if (child.type === 'em_open') marks.push({ type: 'italic' });
      else if (child.type === 's_open') marks.push({ type: 'strike' });
      else if (child.type === 'link_open') marks.push({ type: 'link', attrs: {
        href: child.attrGet('href'), title: child.attrGet('title') || null, target: '_blank', rel: 'noopener noreferrer nofollow', class: null,
      } });
    } else if (child.type.endsWith('_close')) {
      const type = child.type.replace('_close', '').replace('strong', 'bold').replace('em', 'italic').replace('s', 'strike').replace('link', 'link');
      const found = marks.map(mark => mark.type).lastIndexOf(type);
      if (found >= 0) marks.splice(found, 1);
    }
  }
  return result;
}

function textContent(node: EditorNode): string {
  if (node.text) return node.text;
  if (node.type === 'image') return String(node.attrs?.alt || node.attrs?.src || 'image');
  if (node.type === 'inlineMath') return `$${node.attrs?.source || ''}$`;
  if (node.type === 'citation') return `[@${node.attrs?.citationKey || ''}]`;
  return (node.content || []).map(textContent).join(node.type === 'paragraph' ? '' : '\n');
}

function findClose(tokens: Token[], start: number, openType: string, closeType: string) {
  let depth = 0;
  for (let index = start; index < tokens.length; index += 1) {
    if (tokens[index].type === openType) depth += 1;
    if (tokens[index].type === closeType && --depth === 0) return index;
  }
  return tokens.length - 1;
}

function listItem(tokens: Token[], start: number, end: number): EditorNode {
  const content = parseBlocks(tokens, start, end);
  if (!content.length || content[0].type !== 'paragraph') content.unshift({ type: 'paragraph' });
  return { type: 'listItem', content };
}

function parseList(tokens: Token[], start: number, close: number, ordered: boolean): EditorNode {
  const items: EditorNode[] = [];
  let cursor = start + 1;
  while (cursor < close) {
    if (tokens[cursor].type !== 'list_item_open') { cursor += 1; continue; }
    const itemClose = findClose(tokens, cursor, 'list_item_open', 'list_item_close');
    items.push(listItem(tokens, cursor + 1, itemClose));
    cursor = itemClose + 1;
  }
  const tasks = !ordered && items.length > 0 && items.every(item => /^\[[ xX]\]\s+/.test(textContent(item.content![0])));
  if (tasks) {
    const taskItems = items.map(item => {
      const first = item.content![0];
      const raw = textContent(first);
      const checked = /^\[[xX]\]/.test(raw);
      const prefixLength = raw.match(/^\[[ xX]\]\s+/)?.[0].length || 0;
      let remaining = prefixLength;
      for (const child of first.content || []) {
        if (!remaining || !child.text) break;
        const remove = Math.min(remaining, child.text.length);
        child.text = child.text.slice(remove);
        remaining -= remove;
      }
      first.content = (first.content || []).filter(child => child.type !== 'text' || child.text);
      return { ...item, type: 'taskItem', attrs: { checked } };
    });
    return { type: 'taskList', content: taskItems };
  }
  const attrs = ordered ? { start: Number(tokens[start].attrGet('start') || 1) } : undefined;
  return { type: ordered ? 'orderedList' : 'bulletList', attrs, content: items };
}

function parseTable(tokens: Token[], start: number, close: number): EditorNode {
  const rows: EditorNode[] = [];
  let row: EditorNode | null = null;
  let cellType: 'tableHeader' | 'tableCell' | null = null;
  for (let index = start + 1; index < close; index += 1) {
    const token = tokens[index];
    if (token.type === 'tr_open') row = { type: 'tableRow', content: [] };
    else if (token.type === 'tr_close' && row) { rows.push(row); row = null; }
    else if (token.type === 'th_open') cellType = 'tableHeader';
    else if (token.type === 'td_open') cellType = 'tableCell';
    else if (token.type === 'inline' && row && cellType) {
      const style = tokens[index - 1]?.attrGet('style') || '';
      const align = style.match(/text-align:\s*(left|center|right)/)?.[1] || null;
      row.content!.push({ type: cellType, attrs: { textAlign: align }, content: [{ type: 'paragraph', content: inlineNodes(token) }] });
      cellType = null;
    }
  }
  return { type: 'table', content: rows };
}

function parseDefinitionList(tokens: Token[], start: number, close: number): EditorNode {
  const content: EditorNode[] = [];
  let cursor = start + 1;
  while (cursor < close) {
    if (tokens[cursor].type === 'dt_open' && tokens[cursor + 1]?.type === 'inline') {
      content.push({ type: 'definitionTerm', content: inlineNodes(tokens[cursor + 1]) });
      cursor += 3;
    } else if (tokens[cursor].type === 'dd_open') {
      const ddClose = findClose(tokens, cursor, 'dd_open', 'dd_close');
      content.push({ type: 'definitionDescription', content: parseBlocks(tokens, cursor + 1, ddClose) });
      cursor = ddClose + 1;
    } else cursor += 1;
  }
  return { type: 'definitionList', content };
}

function parseFootnotes(tokens: Token[], start: number, close: number): EditorNode[] {
  const result: EditorNode[] = [{ type: 'horizontalRule' }];
  let cursor = start + 1;
  while (cursor < close) {
    if (tokens[cursor].type !== 'footnote_open') { cursor += 1; continue; }
    const footnoteClose = findClose(tokens, cursor, 'footnote_open', 'footnote_close');
    const label = String(tokens[cursor].meta?.label || Number(tokens[cursor].meta?.id || 0) + 1);
    const inner = tokens.slice(cursor + 1, footnoteClose).filter(token => token.type !== 'footnote_anchor');
    result.push({ type: 'footnoteDefinition', attrs: { label }, content: parseBlocks(inner, 0, inner.length) });
    cursor = footnoteClose + 1;
  }
  return result;
}

export function parseBlocks(tokens: Token[], start = 0, end = tokens.length): EditorNode[] {
  const result: EditorNode[] = [];
  let index = start;
  while (index < end) {
    const token = tokens[index];
    if (token.type === 'paragraph_open' && tokens[index + 1]?.type === 'inline') {
      result.push({ type: 'paragraph', content: inlineNodes(tokens[index + 1]) }); index += 3;
    } else if (token.type === 'heading_open' && tokens[index + 1]?.type === 'inline') {
      result.push({ type: 'heading', attrs: { level: Number(token.tag.slice(1)) }, content: inlineNodes(tokens[index + 1]) }); index += 3;
    } else if (token.type === 'blockquote_open') {
      const close = findClose(tokens, index, 'blockquote_open', 'blockquote_close');
      result.push({ type: 'blockquote', content: parseBlocks(tokens, index + 1, close) }); index = close + 1;
    } else if (token.type === 'bullet_list_open' || token.type === 'ordered_list_open') {
      const closeType = token.type.replace('_open', '_close');
      const close = findClose(tokens, index, token.type, closeType);
      result.push(parseList(tokens, index, close, token.type === 'ordered_list_open')); index = close + 1;
    } else if (token.type === 'table_open') {
      const close = findClose(tokens, index, 'table_open', 'table_close');
      result.push(parseTable(tokens, index, close)); index = close + 1;
    } else if (token.type === 'dl_open') {
      const close = findClose(tokens, index, 'dl_open', 'dl_close');
      result.push(parseDefinitionList(tokens, index, close)); index = close + 1;
    } else if (token.type === 'footnote_block_open') {
      const close = findClose(tokens, index, 'footnote_block_open', 'footnote_block_close');
      result.push(...parseFootnotes(tokens, index, close)); index = close + 1;
    } else if (token.type === 'fence') {
      const language = token.info.trim().split(/\s+/)[0].toLowerCase();
      if (language === 'mermaid') result.push({ type: 'mermaidBlock', attrs: { source: token.content.trimEnd() } });
      else if (language === 'math' || language === 'latex' || language === 'tex') result.push({ type: 'mathBlock', attrs: { source: token.content.trimEnd() } });
      else result.push({ type: 'codeBlock', attrs: { language: language || null }, content: token.content ? [{ type: 'text', text: token.content.replace(/\n$/, '') }] : undefined });
      index += 1;
    } else if (token.type === 'code_block') {
      result.push({ type: 'codeBlock', attrs: { language: null }, content: [{ type: 'text', text: token.content.replace(/\n$/, '') }] }); index += 1;
    } else if (token.type === 'math_block') {
      result.push({ type: 'mathBlock', attrs: { source: token.content.trim() } }); index += 1;
    } else if (token.type === 'html_block') {
      const secret = token.content.trim().match(/^<!--ourora-secret:(\d+)-->$/);
      result.push(secret
        ? { type: 'secretPlaceholder', attrs: { index: Number(secret[1]) } }
        : { type: 'codeBlock', attrs: { language: 'html' }, content: [{ type: 'text', text: token.content.trimEnd() }] });
      index += 1;
    } else if (token.type === 'hr') { result.push({ type: 'horizontalRule' }); index += 1; }
    else index += 1;
  }
  return result;
}

export function markdownToEditor(markdownSource: string): EditorNode[] {
  return parseBlocks(markdown.parse(markdownSource, {}));
}

export function editorNodeText(node: EditorNode) { return textContent(node); }
